"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildVideoSpec = exports.resolveSceneOrder = exports.wrapContext = void 0;
const vlx_types_1 = require("@sundaysky/vlx-types");
const logic_1 = require("./logic");
const consts_1 = require("./consts");
const interactivity_1 = require("./interactivity");
const logger_1 = require("@sundaysky/logger");
const log = (0, logger_1.logger)("specbuilder");
const DEFAULT_QUALITY = "sd";
const buildSceneSpec = (name, sceneDefinition, context, mediaResolver) => {
    // resolve animation
    let animation = (0, logic_1.resolveVar)(sceneDefinition.animation, context, mediaResolver).value;
    // resolve scene level variables
    if (sceneDefinition.variables) {
        // wipe scene variables from previous scenes
        context.dataElements[vlx_types_1.SCOPE.Scene] = null;
        // vars should be an array of data-elements
        (0, logic_1.addVariablesToContext)(sceneDefinition.variables, context, [vlx_types_1.SCOPE.Scene], mediaResolver);
    }
    // resolve data
    let data = (0, logic_1.resolveLogic)(sceneDefinition.data, context, mediaResolver);
    return {
        name,
        animation,
        data
    };
};
// todo - use wrapContext from dataContext
const wrapContext = dataContext => {
    const DATAELEMENTS = "dataElements";
    // wrap the data context first, to match how hub defines logic
    // back compatibility
    if (dataContext && dataContext[DATAELEMENTS] && Object.keys(dataContext).length == 1) {
        return dataContext;
    }
    else {
        return { [DATAELEMENTS]: dataContext };
    }
};
exports.wrapContext = wrapContext;
const isTrueCondition = (item, context) => {
    return item === undefined || item === true || (0, logic_1.resolveBoolean)(item, context);
};
const buildLineupSpec = (lineupDefinition, context, mediaResolver, scenePostProcess) => {
    let lineupSpec = [];
    let validity = {};
    for (let i = 0; i < lineupDefinition.length; i++) {
        let item = lineupDefinition[i];
        // is scene valid?
        if (item.scene) {
            // then, convert to scene spec and add to list
            let sceneSpec = buildSceneSpec(item.name, item.scene, context, mediaResolver);
            if (scenePostProcess && sceneSpec) {
                scenePostProcess(sceneSpec, context);
            }
            if (isTrueCondition(item.scene.valid, context)) {
                validity[item.name] = true;
                lineupSpec.push(sceneSpec);
                log.info("added scene", sceneSpec.name);
            }
            else {
                validity[item.name] = false;
            }
        }
        else {
            validity[item.name] = false;
        }
    }
    log.info("lineup", lineupSpec);
    lineupSpec[vlx_types_1.METADATA] = { valid: validity };
    //Sometimes the last scene is not valid, so the last valid scene before it should be marked as the last scene.
    //Same for first scene.
    //These are parameters, or flags, used by the animation.
    //At this point, only valid scenes appear in the lineup.
    if (lineupSpec.length > 0) {
        lineupSpec.forEach((scene) => {
            scene.data.isLastScene = "false";
            scene.data.isFirstScene = "false";
        });
        lineupSpec[lineupSpec.length - 1].data.isLastScene = "true";
        lineupSpec[0].data.isFirstScene = "true";
    }
    return lineupSpec;
};
const resolveSceneOrder = (sceneOrderDef, lineupDef, context) => {
    if (!sceneOrderDef || !sceneOrderDef.start) {
        // if sceneOrdering not valid, fallback to the order in the lineup field
        log.info("no sceneOrder (or not valid), using lineup order");
        return lineupDef;
    }
    let scenes = []; // the final order of scenes
    let traversed = {}; // keep track of visited states, to avoid loops
    let state; // the current state
    let moveToState = (statename) => {
        if (!statename) {
            state = null;
            return;
        }
        if (traversed[statename]) {
            throw new Error(`endless loop! ${statename} reached twice`);
        }
        traversed[statename] = true;
        // Lazy resolve next state's logic
        sceneOrderDef[statename] = (0, logic_1.resolveLogic)(sceneOrderDef[statename], context);
        state = sceneOrderDef[statename];
        if (statename && !state) {
            log.error(`possible error in sceneOrder? ${statename} not present`);
        }
    };
    moveToState("start");
    while (state) {
        // add scenes from this state
        if (state.scenes) {
            scenes.push(...state.scenes);
        }
        // proceed to next
        moveToState(state.next);
    }
    log.info("sceneOrder", scenes);
    // convert lineup to map to make it easy to find scenes
    let allLineupItems = lineupDef.reduce((o, l) => (o[l.name] = l) && o, {});
    // convert scenes to array of lineup items
    let lineup = scenes
        .map(s => allLineupItems[s])
        .filter(li => (li));
    return lineup;
};
exports.resolveSceneOrder = resolveSceneOrder;
const replaceHotspotIds = (retLineup) => {
    // replaces hotspot objects in prioritized list with hotspot ID *in* story.lineup object
    retLineup.forEach(scene => {
        Object.keys(scene.data).forEach(dataItemName => {
            if (scene.data[dataItemName] && scene.data[dataItemName][consts_1.VALID_SLOTS] !== undefined) {
                let pl = scene.data[dataItemName];
                Object.keys(pl).forEach(slotName => {
                    if (typeof pl[slotName] === "object") {
                        Object.keys(pl[slotName]).forEach(phName => {
                            if (pl[slotName][phName].id && pl[slotName][phName].type) {
                                pl[slotName][phName] = { actionRef: pl[slotName][phName].id };
                            }
                        });
                    }
                });
            }
        });
    });
};
const addSettingsToScene = (scenedef, story) => {
    if (story.transition && !scenedef.transition) {
        scenedef.transition = story.transition;
    }
    else if (!scenedef.transition) {
        // hard code a stupid default
        scenedef.transition = {
            "duration": ".48",
            "style": "overlay"
        };
    }
};
const buildVideoSpec = (story, context, mediaResolver) => {
    var _a, _b, _c, _d, _e;
    // the passed context may not have already been wrapped in a 'dataelements' section
    const dataContext = (0, exports.wrapContext)(context);
    // scene order?
    if (story.sceneOrder) {
        story.lineup = (0, exports.resolveSceneOrder)(story.sceneOrder, story.lineup, dataContext);
    }
    // set descriptive transcript output setting
    if (story.lineup && story.lineup.some(sceneObj => sceneObj.scene && sceneObj.scene.data && sceneObj.scene.data[consts_1.DESCRIPTIVE_TRANSCRIPT])) {
        story.settings = story.settings || {};
        story.settings.output = story.settings.output || {};
        story.settings.output[consts_1.DESCRIPTIVE_TRANSCRIPT] = true;
    }
    // build lineup
    let videoSpec = {
        lineup: buildLineupSpec(story.lineup, dataContext, mediaResolver, (scene) => addSettingsToScene(scene, story))
    };
    // copy in settings to top level
    if (story.settings) {
        // resolve any logic in settings
        let settings = (0, logic_1.resolveLogic)(story.settings, dataContext);
        Object.keys(settings)
            .forEach(k => videoSpec[k] = settings[k]);
    }
    // set (or override) AspectRatio output setting from data elements
    if ((_a = dataContext === null || dataContext === void 0 ? void 0 : dataContext.dataElements) === null || _a === void 0 ? void 0 : _a.videoAspectRatio) {
        videoSpec.output = videoSpec.output || {};
        videoSpec.output.videoAspectRatio = dataContext.dataElements.videoAspectRatio;
    }
    // set (or override) QualityProfile output setting from data elements - default to sd
    if ((_b = videoSpec.output) === null || _b === void 0 ? void 0 : _b.videoAspectRatio) {
        if ((_c = dataContext === null || dataContext === void 0 ? void 0 : dataContext.dataElements) === null || _c === void 0 ? void 0 : _c.videoQualityProfile) {
            videoSpec.output.videoQualityProfile = dataContext.dataElements.videoQualityProfile;
        }
        else if (!videoSpec.output.videoQualityProfile) {
            videoSpec.output.videoQualityProfile = DEFAULT_QUALITY;
        }
    }
    // delete AspectRatio & QualityProfile output settings if videoAspectRatio isn't valid
    else {
        if (((_d = videoSpec.output) === null || _d === void 0 ? void 0 : _d.videoQualityProfile) !== undefined) {
            delete videoSpec.output.videoQualityProfile;
        }
        if (((_e = videoSpec.output) === null || _e === void 0 ? void 0 : _e.videoAspectRatio) !== undefined) {
            delete videoSpec.output.videoAspectRatio;
        }
    }
    // metadata (hotspot)
    const newInteractivity = (0, interactivity_1.createInteractivity)(videoSpec.lineup, story.lineup, dataContext);
    if (Object.keys(newInteractivity.actionDefs).length) {
        replaceHotspotIds(videoSpec.lineup);
        videoSpec.metadata = videoSpec.metadata || {};
        videoSpec.metadata.interactivity = newInteractivity;
    }
    return videoSpec;
};
exports.buildVideoSpec = buildVideoSpec;
//# sourceMappingURL=specbuilder.js.map